import { DateTime } from 'luxon'

import { SubscriptionItem } from '@/core/api/entities/account/subscriptionItem'
import { SubscriptionPlan } from '@/core/api/entities/account/subscriptionPlan'
import { SubscriptionPlanPrice } from '@/core/api/entities/account/subscriptionPlanPrice'
import {
	PRODUCT_BOX_ADDON_ID,
	PRODUCT_BOX_ONE_SHOT_ID,
	PRODUCT_PLAYER_ID,
	PRODUCT_SOUNDREEF_ADDON_ID,
} from '@/core/api/entities/misc/product'

import { StatusUpgrade } from './statusUpgrade'

export enum SubscriptionStatus {
	ACTIVE = 'active',
	FUTURE = 'future',
	IN_TRIAL = 'in_trial',
	PAST_DUE = 'past_due',
	CANCELLED = 'cancelled',
	EXPIRED = 'expired',
}
export class Subscription {
	public id: number
	public accountId: number
	public planId: number
	public startAt: DateTime
	public endAt: DateTime | null
	public trialStartAt: DateTime | null
	public trialEndAt: DateTime | null
	public firstBillingAt: DateTime | null
	public nextBillingAt: DateTime | null
	public periodCycle: string
	public currency: string
	public poNumber: string | null
	public poNumberRequired: boolean
	public invoiceNotes: string | null
	public status: SubscriptionStatus
	public total: number
	public createdAt: DateTime
	public updatedAt: DateTime
	public hasLicenses: boolean
	public items: SubscriptionItem[]
	public plan?: SubscriptionPlan

	constructor(data: {
		id: number
		accountId: number
		planId: number
		startAt: DateTime
		endAt: DateTime | null
		trialStartAt: DateTime | null
		trialEndAt: DateTime | null
		firstBillingAt: DateTime | null
		nextBillingAt: DateTime | null
		periodCycle: string
		currency: string
		poNumber: string | null
		poNumberRequired: boolean
		invoiceNotes: string | null
		status: SubscriptionStatus
		total: number
		createdAt: DateTime
		updatedAt: DateTime
		hasLicenses: boolean
		items: SubscriptionItem[]
		plan?: SubscriptionPlan
	}) {
		this.id = data.id
		this.accountId = data.accountId
		this.planId = data.planId
		this.startAt = data.startAt
		this.endAt = data.endAt
		this.trialStartAt = data.trialStartAt
		this.trialEndAt = data.trialEndAt
		this.firstBillingAt = data.firstBillingAt
		this.nextBillingAt = data.nextBillingAt
		this.periodCycle = data.periodCycle
		this.currency = data.currency
		this.poNumber = data.poNumber
		this.poNumberRequired = data.poNumberRequired
		this.invoiceNotes = data.invoiceNotes
		this.status = data.status
		this.total = data.total
		this.createdAt = data.createdAt
		this.updatedAt = data.updatedAt
		this.hasLicenses = data.hasLicenses
		this.items = data.items
		this.plan = data.plan
	}

	static create(data: Partial<Subscription>) {
		const defaultData = {
			id: 0,
			accountId: 0,
			planId: 0,
			startAt: DateTime.now(),
			endAt: null,
			trialStartAt: null,
			trialEndAt: null,
			firstBillingAt: null,
			nextBillingAt: null,
			periodCycle: '',
			currency: '',
			poNumber: null,
			poNumberRequired: false,
			invoiceNotes: null,
			status: SubscriptionStatus.ACTIVE,
			total: 0,
			createdAt: DateTime.now(),
			updatedAt: DateTime.now(),
			hasLicenses: false,
			items: [],
		}

		return new Subscription({ ...defaultData, ...data })
	}

	// Return the items of the product received
	getProductItems(productId: number): SubscriptionItem[] {
		return this.items.filter((item) => item.productId === productId)
	}

	// Return the items of the player product
	getPlayerItems(): SubscriptionItem[] {
		// An ugly workaround to not query also the products, since we're running out of time and it is very unlikely to change the product_id
		// TODO: Find a better way to do this
		return this.getProductItems(PRODUCT_PLAYER_ID)
	}

	// Return the items of the addon product
	getAddonItems(): SubscriptionItem[] {
		// An ugly workaround to not query also the products, since we're running out of time and it is very unlikely to change the product_id
		// TODO: Find a better way to do this
		return this.getProductItems(PRODUCT_BOX_ADDON_ID)
	}

	// Return the items of the soundreef product
	getSoundreefItems(): SubscriptionItem[] {
		// An ugly workaround to not query also the products, since we're running out of time and it is very unlikely to change the product_id
		// TODO: Find a better way to do this
		return this.getProductItems(PRODUCT_SOUNDREEF_ADDON_ID)
	}

	// Return the original price of the product received. It only considers the price list (subscription plan price)
	getProductOriginalPrice(productId: number): number | null {
		// If the plan or its prices are not set, we can't calculate any price
		if (!this.plan || !this.plan.prices || this.plan.prices.length === 0) {
			return null
		}

		const items = this.getProductItems(productId)
		if (items.length === 0) {
			return null
		}

		const prices = this.plan.prices.filter((price: SubscriptionPlanPrice) => {
			return (
				price.productId === items[0].productId &&
				price.planId === this.plan?.id &&
				price.currency === this.currency
			)
		})

		if (prices.length === 0) {
			return null
		}

		return prices[0].price
	}

	// Return the original price of players. It only considers the price list (subscription plan price)
	getPlayerOriginalPrice(): number | null {
		return this.getProductOriginalPrice(PRODUCT_PLAYER_ID)
	}

	// Return the original price of addons. It only considers the price list (subscription plan price)
	getAddonOriginalPrice(): number | null {
		return this.getProductOriginalPrice(PRODUCT_BOX_ADDON_ID)
	}

	// Return the price  of the product received, considering if it has a custom price or it has a discount related
	getProductPrice(productId: number): number | null {
		// If the plan or its prices are not set, we can't calculate any price
		if (!this.plan || !this.plan.prices || this.plan.prices.length === 0) {
			return null
		}

		const items = this.getProductItems(productId)
		if (items.length === 0) {
			return null
		}

		// Get propper amount
		const amount =
			items[0].getAmount(this.plan.prices, this.planId, this.currency) ?? 0

		//Return the price considering the discount
		return (amount * (100 - items[0].discount)) / 100
	}

	// Return the total amount of items (quantity) of the product received
	getProductQuantity(productId: number): number | null {
		// If the plan or its prices are not set, we can't calculate any price
		if (!this.plan || !this.plan.prices || this.plan.prices.length === 0) {
			return null
		}

		const items = this.getProductItems(productId)
		if (items.length === 0) {
			return 0
		}

		// Return quantity
		let total = 0
		items.map((item) => (total += item.quantity))

		return total
	}

	// Get the price of players in the subscription, considering if it is a custom price or has a discount associated
	getZonePrice(): number | null {
		return this.getProductPrice(PRODUCT_PLAYER_ID)
	}

	// Get the price of addons in the subscription, considering if it is a custom price or has a discount associated
	getAddonPrice(): number | null {
		return this.getProductPrice(PRODUCT_BOX_ADDON_ID)
	}

	// Get the total amount of players in the subscription
	getPlayerQuantity(): number | null {
		return this.getProductQuantity(PRODUCT_PLAYER_ID)
	}

	// Get the total amount of addons in the subscription
	getAddonQuantity(): number | null {
		return this.getProductQuantity(PRODUCT_BOX_ADDON_ID)
	}

	// Get the total amount of soundreef licenses in the subscription
	getSoundreefQuantity(): number | null {
		return this.getProductQuantity(PRODUCT_SOUNDREEF_ADDON_ID)
	}

	// Return total doing a multiplication between the player quantity and its price (considering discounts)
	getPlayerTotal(): number | null {
		return (this.getZonePrice() ?? 0) * (this.getPlayerQuantity() ?? 0)
	}

	// Return total doing a multiplication between the player addomn and its price (considering discounts)
	getAddonTotal(): number | null {
		return (this.getAddonPrice() ?? 0) * (this.getAddonQuantity() ?? 0)
	}

	// Return total by summing the player total and the addon total
	getTotal(): number | null {
		return (this.getPlayerTotal() ?? 0) + (this.getAddonTotal() ?? 0)
	}

	// Returna box price based on the current subscription items or the prices list, if the account has a compatible box mode
	getBoxPrice(
		accountUpgradeStatus: StatusUpgrade,
		prices: SubscriptionPlanPrice[] | null
	): number | null {
		const boxPrice = this.getAddonPrice()
		if (boxPrice) {
			return boxPrice
		}

		// Pick the product based on the account box mode
		const productId = accountUpgradeStatus.isOneShotMode
			? PRODUCT_BOX_ONE_SHOT_ID
			: PRODUCT_BOX_ADDON_ID

		// Filter prices by the desired product id
		const filteredPrices = prices?.filter((price) => {
			return price.productId === productId
		})

		return filteredPrices && filteredPrices.length > 0
			? filteredPrices[0].price
			: null
	}
}
